import { supabase } from './supabase';
import { DISABLE_EMAIL_NOTIFICATIONS } from './constants';
import { SUPABASE_ANON_KEY } from './constants';

interface EmailNotification {
  id: string;
  service_request_id?: string;
  visit_id?: string;
  recipient: string;
  subject: string;
  body: string;
  sent_at: string;
  status: string;
}

type EmailSendOptions = {
  cc?: string | string[];
  replyTo?: string | string[];
};

interface NewUserPayload {
  fullname: string;
  email: string;
  phoneNumber: string;
}

const GOOGLE_CLOUD_FUNCTION_URL = 'https://wqznjpzkejoxiokudivc.supabase.co/functions/v1/send-email';

export const getEmailNotifications = async (serviceRequestId: string): Promise<EmailNotification[]> => {
  const { data, error } = await supabase
    .from('email_notifications')
    .select('*')
    .eq('service_request_id', serviceRequestId)
    .order('sent_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

export const sendServiceRequestEmail = async (
  notification: EmailNotification,
  options?: EmailSendOptions
): Promise<void> => {
  // Check if email notifications are disabled
  if (DISABLE_EMAIL_NOTIFICATIONS) {
    console.log('[Email Notifications Disabled] Skipping email send:', notification.subject);
    
    // Update notification status to indicate it was skipped
    await supabase
      .from('email_notifications')
      .update({ status: 'skipped' })
      .eq('id', notification.id);
    
    return;
  }

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      throw new Error('Not authenticated. Missing access token for email send.');
    }

    const payload = {
      to: notification.recipient,
      subject: notification.subject,
      text: notification.body,
      cc: options?.cc,
      replyTo: options?.replyTo,
    };

    // Prefer Supabase Functions invoke because it automatically includes auth headers.
    const { error: functionError } = await supabase.functions.invoke('send-email', {
      body: payload,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON_KEY,
      },
    });

    if (functionError) {
      // Fallback to direct fetch for environments where invoke may fail unexpectedly.
      const response = await fetch(GOOGLE_CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to send email: ${functionError.message || 'invoke failed'} | ${errorText || response.statusText || 'unknown error'}`
        );
      }
    }

    // Update notification status
    const { error: updateError } = await supabase
      .from('email_notifications')
      .update({ status: 'sent' })
      .eq('id', notification.id);

    if (updateError) {
      throw updateError;
    }

    console.log('Email sent successfully');
  } catch (error) {
    console.error('Failed to send email:', error);
    
    // Update notification status to failed
    await supabase
      .from('email_notifications')
      .update({ status: 'failed' })
      .eq('id', notification.id);

    throw error;
  }
};

export const resendServiceRequestEmail = async (serviceRequestId: string): Promise<void> => {
  const { data: request, error: requestError } = await supabase
    .from('service_requests')
    .select(`
      *,
      homes (
        address,
        city,
        state
      )
    `)
    .eq('id', serviceRequestId)
    .single();

  if (requestError) {
    throw requestError;
  }

  const emailBody = `
A new service request has been submitted:

Title: ${request.title}
Description: ${request.description}
Priority: ${request.priority}
Status: ${request.status}
Location: ${request.homes.address}, ${request.homes.city}, ${request.homes.state}

Please review and respond accordingly.
  `.trim();

  const { data: notification, error: notificationError } = await supabase
    .from('email_notifications')
    .insert([{
      service_request_id: serviceRequestId,
      recipient: 'service@honeydone.com',
      subject: `New Service Request: ${request.title}`,
      body: emailBody
    }])
    .select()
    .single();

  if (notificationError) {
    throw notificationError;
  }

  // Send the email
  await sendServiceRequestEmail(notification);
};

export const sendNewUserRegistrationEmail = async (payload: NewUserPayload): Promise<void> => {
  const subject = `New User Registration: ${payload.fullname}`;
  const body = `
A new user has registered:

Full Name: ${payload.fullname}
Email: ${payload.email}
Phone: ${payload.phoneNumber}
  `.trim();

  // Insert the email notification record
  const { data: notification, error: notificationError } = await supabase
    .from('email_notifications')
    .insert([{
      recipient: 'service@honeydone.com',
      subject,
      body
    }])
    .select()
    .single();

  if (notificationError) {
    throw notificationError;
  }

  // Send the email using the existing function
  await sendServiceRequestEmail(notification);
};

export const sendVisitNotificationEmail = async (visitId: string): Promise<void> => {
  try {
    // Get visit details with home and owner information
    const { data: visit, error: visitError } = await supabase
      .from('visits')
      .select(`
        *,
        homes (
          address,
          city,
          state,
          zip_code,
          profiles (
            full_name,
            phone_number
          )
        )
      `)
      .eq('id', visitId)
      .single();

    if (visitError) {
      throw visitError;
    }

    const visitTypeDisplay = visit.visit_type === 'maintenance' 
      ? 'Maintenance Visit' 
      : visit.visit_type === 'walkthrough'
      ? 'Walkthrough'
      : 'Service Visit';

    const emailBody = `
A new visit request has been submitted:

Visit Type: ${visitTypeDisplay}
Status: ${visit.status}
Scheduled Date: ${visit.scheduled_date ? new Date(visit.scheduled_date).toLocaleString() : 'Not yet scheduled'}
Assigned To: ${visit.assigned_to || 'Unassigned'}

Property Information:
Address: ${visit.homes.address}, ${visit.homes.city}, ${visit.homes.state} ${visit.homes.zip_code}
Owner: ${visit.homes.profiles.full_name}
Phone: ${visit.homes.profiles.phone_number || 'N/A'}

Notes: ${visit.visit_notes || 'None'}

Please review and respond accordingly.
    `.trim();

    const { data: notification, error: notificationError } = await supabase
      .from('email_notifications')
      .insert([{
        visit_id: visitId,
        recipient: 'service@honeydone.com',
        subject: `New Visit Request: ${visitTypeDisplay} - ${visit.homes.address}`,
        body: emailBody
      }])
      .select()
      .single();

    if (notificationError) {
      throw notificationError;
    }

    // Send the email
    await sendServiceRequestEmail(notification);
  } catch (error) {
    console.error('Failed to send visit notification email:', error);
    throw error;
  }
};

export const getVisitEmailNotifications = async (visitId: string): Promise<EmailNotification[]> => {
  const { data, error } = await supabase
    .from('email_notifications')
    .select('*')
    .eq('visit_id', visitId)
    .order('sent_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};